import { useReactFlow } from '@xyflow/react';
import useDirty from './use-dirty';
import useSidebar from './use-sidebar';
import useStore from './use-store';

/**
 * Errors on pristine nodes stay hidden until the user tries to save the playbook. `reveal` shows them
 * everywhere and opens the first offending node. An action they block (save, create, test) stays enabled
 * while any are hidden, even beside visible errors, so one click lands on every fix at once instead of a
 * disabled tooltip naming errors nobody can see.
 */
const useHiddenNodeErrors = () => {
  const {
    data: { banners, graph, pristineNodeIds },
    revealNodeErrors,
  } = useStore();
  const { open, isOpen } = useSidebar();
  const { isAnyComponentDirty } = useDirty();
  const reactFlow = useReactFlow();

  const nodeIdsWithHiddenErrors = graph.nodes
    .filter(node => pristineNodeIds.includes(node.id))
    .filter(node => banners.some(b => b.nodeId === node.id && b.type === 'error'))
    .map(node => node.id);

  const reveal = () => {
    if (nodeIdsWithHiddenErrors.length === 0) return;
    revealNodeErrors();
    // The backend sends nodes in no stable order, so take the top-most. A node in a collapsed branch isn't
    // rendered, so opening it would show nothing.
    const rendered = nodeIdsWithHiddenErrors
      .flatMap(id => {
        const node = reactFlow.getNode(id);
        return node && !node.hidden ? [node] : [];
      })
      .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
    const target = (rendered.find(node => isOpen(node.id)) ?? rendered[0])?.id;
    // Switching nodes would drop another panel's unsaved edits without the usual confirm.
    if (target && !isAnyComponentDirty) open(target, { focusNode: true });
  };

  return { hasHiddenErrors: nodeIdsWithHiddenErrors.length > 0, reveal };
};

export default useHiddenNodeErrors;
